'use client'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { isAxiosError } from 'axios'
import api from '@/lib/api'
import { useAuthStore } from '@/context/auth.store'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useI18n } from '@/components/I18nProvider'

type LoginInput = {
  email: string
  password: string
  otp_code?: string
}

type LoginError =
  | { kind: 'generic'; message: string }
  | { kind: 'unverified'; email: string }

export function LoginForm() {
  const router = useRouter()
  const login = useAuthStore((state) => state.login)
  const [isLoading, setIsLoading] = useState(false)
  const [loginError, setLoginError] = useState<LoginError | null>(null)
  const [needsOtp, setNeedsOtp] = useState(false)
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const { t } = useI18n()

  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(t({ es: 'Email invalido', en: 'Invalid email' })),
        password: z
          .string()
          .min(8, t({ es: 'Minimo 8 caracteres', en: 'At least 8 characters' })),
        otp_code: z.string().optional(),
      }),
    [t]
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  const handleResendVerification = async () => {
    const email = getValues('email')
    if (!email || resendState === 'sending') return
    setResendState('sending')
    try {
      await api.post('/auth/resend-verification', { email })
      setResendState('sent')
    } catch {
      setResendState('error')
    }
  }

  const onSubmit = async (data: LoginInput) => {
    if (isLoading) return
    setIsLoading(true)
    setLoginError(null)
    setResendState('idle')
    try {
      const startedAt = new Date().getTime()
      sessionStorage.setItem('bpal_login_flow_start_epoch_ms', String(startedAt))
      await login(data.email, data.password, data.otp_code)
      // Force full document navigation to avoid transient client-side tree contamination
      // seen during SPA transitions after auth handoff.
      window.location.href = '/dashboard'
    } catch (err) {
      const rawDetail = isAxiosError<{ detail?: string }>(err)
        ? err.response?.data?.detail
        : undefined
      const statusCode = isAxiosError(err) ? err.response?.status : undefined
      const detailLower = String(rawDetail || '').toLowerCase()

      const isOtpChallenge =
        detailLower.includes('verification code') ||
        detailLower.includes('otp') ||
        detailLower.includes('2fa')
      const isEmailUnverified =
        statusCode === 403 &&
        (detailLower.includes('verify your email') ||
          detailLower.includes('email before signing') ||
          detailLower.includes('email not verified'))
      const isNetworkFailure =
        detailLower.includes('network error') ||
        detailLower.includes('timeout') ||
        detailLower.includes('aborted') ||
        detailLower.includes('ns_binding_aborted')

      if (isEmailUnverified) {
        setLoginError({ kind: 'unverified', email: data.email })
        setNeedsOtp(false)
        setIsLoading(false)
        return
      }

      const message =
        statusCode === 401 && isOtpChallenge
          ? t({
              es: 'Introduce tu codigo de verificacion para continuar.',
              en: 'Enter your verification code to continue.',
            })
          : statusCode === 401
            ? t({
                es: 'Email o contrasena incorrectos.',
                en: 'Email or password is incorrect.',
              })
            : statusCode === 403 && detailLower.includes('pending_approval')
              ? t({
                  es: 'Tu cuenta esta pendiente de aprobacion. Revisa tu email o contacta soporte.',
                  en: 'Your account is pending approval. Check your email or contact support.',
                })
              : statusCode === 403 && detailLower.includes('rejected')
                ? t({
                    es: 'Tu registro fue rechazado. Contacta soporte si crees que es un error.',
                    en: 'Your registration was rejected. Contact support if you think this is a mistake.',
                  })
                : statusCode === 403
                  ? t({
                      es: 'Tu cuenta no esta disponible. Contacta soporte si crees que es un error.',
                      en: 'Your account is unavailable. Contact support if you think this is a mistake.',
                    })
                  : statusCode === 429
                    ? t({
                        es: 'Demasiados intentos. Espera un momento e intentalo de nuevo.',
                        en: 'Too many attempts. Please wait and try again.',
                      })
                    : isNetworkFailure
                      ? t({
                          es: 'No pudimos conectar con el servidor. Intentalo de nuevo en unos segundos.',
                          en: 'We could not reach the server. Please try again in a few seconds.',
                        })
                      : t({ es: 'No se pudo iniciar sesion.', en: 'Unable to sign in.' })

      setLoginError({ kind: 'generic', message })
      setNeedsOtp(Boolean(isOtpChallenge))
      setIsLoading(false)
    }
  }

  const formError = loginError?.kind === 'generic' ? loginError.message : null

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-1.5">
      <Input
        label={t({ es: 'Email', en: 'Email' })}
        type="email"
        autoComplete="username email"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        placeholder={t({ es: 'tu@email.com', en: 'you@email.com' })}
        error={errors.email?.message}
        className="rounded-2xl border-[#99f6e4] bg-[#f0fdfa] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:ring-[#14b8a6]/10"
        {...register('email')}
      />

      <Input
        label={t({ es: 'Contrasena', en: 'Password' })}
        type="password"
        autoComplete="current-password"
        placeholder="********"
        error={errors.password?.message || formError || undefined}
        className="rounded-2xl border-black/0 bg-[#f2f7f7] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:bg-white focus:ring-[#14b8a6]/10"
        {...register('password')}
      />

      {needsOtp ? (
        <Input
          label={t({ es: 'Codigo 2FA', en: '2FA code' })}
          autoComplete="one-time-code"
          inputMode="numeric"
          placeholder="123456"
          error={errors.otp_code?.message}
          className="rounded-2xl border-[#99f6e4] bg-[#f0fdfa] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:ring-[#14b8a6]/10"
          {...register('otp_code')}
        />
      ) : null}

      {loginError?.kind === 'unverified' ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3 text-sm">
          <p className="font-semibold text-amber-900">
            {t({ es: 'Tu correo no está verificado.', en: 'Your email address has not been verified.' })}
          </p>
          <p className="text-amber-800">
            {t({
              es: 'Debes verificar tu correo para poder iniciar sesión.',
              en: 'You need to verify your email before you can sign in.',
            })}
          </p>
          {resendState === 'sent' ? (
            <p className="text-emerald-700 font-medium">
              {t({ es: '✓ Enlace de verificación enviado. Revisa tu bandeja de entrada.', en: '✓ Verification email sent. Check your inbox.' })}
            </p>
          ) : resendState === 'error' ? (
            <p className="text-red-700">
              {t({ es: 'No pudimos enviar el correo. Inténtalo de nuevo.', en: 'We could not send the email. Please try again.' })}
            </p>
          ) : null}
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="w-full"
              isLoading={resendState === 'sending'}
              disabled={resendState === 'sending' || resendState === 'sent'}
              onClick={() => void handleResendVerification()}
            >
              {resendState === 'sent'
                ? t({ es: 'Enviado ✓', en: 'Sent ✓' })
                : t({ es: 'Reenviar correo de verificación', en: 'Resend verification email' })}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                setLoginError(null)
                setResendState('idle')
              }}
            >
              {t({ es: 'Usar otro email', en: 'Use another email' })}
            </Button>
          </div>
        </div>
      ) : null}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="mt-2 w-full rounded-2xl bg-[linear-gradient(135deg,#0f766e,#14b8a6_58%,#2dd4bf)] py-3 text-[15px] shadow-[0_22px_50px_-28px_rgba(13,148,136,0.56)] hover:translate-y-0"
        isLoading={isLoading}
        disabled={isLoading}
      >
        {isLoading
          ? t({ es: 'Entrando...', en: 'Signing in...' })
          : t({ es: 'Continuar', en: 'Continue' })}
      </Button>

      <div className="space-y-1.5 pt-1 text-center text-sm">
        <Link href="/forgot-password" className="font-medium text-[#0f766e] hover:underline">
          {t({ es: 'He olvidado mi contrasena', en: 'I forgot my password' })}
        </Link>
        {formError ? (
          <p className="text-muted">
            {t({
              es: 'Si no recuerdas tu acceso, recupera tu contrasena por email.',
              en: 'If you do not remember your access, recover your password by email.',
            })}
          </p>
        ) : null}
      </div>
    </form>
  )
}
